import { ArrowLeft, Mail, Phone, MapPin, MessageSquare, ChevronDown, Search, FileText, Users, Shield, Zap, Star, Clock, Building2 } from 'lucide-react';
import { useState } from 'react';

type InfoPage = 'about' | 'contact' | 'faq' | 'blog' | 'help';

type Props = {
  page: InfoPage;
  onBack: () => void;
  onNavigate: (page: InfoPage) => void;
};

const ABOUT_CONTENT = {
  stats: [
    { value: '30,000+', label: 'Users' },
    { value: '500,000+', label: 'Applications processed' },
    { value: '99.9%', label: 'Uptime' },
    { value: '4.9/5', label: 'Average rating' },
  ],
  values: [
    { icon: Users, title: 'People first', desc: 'Every feature we build starts with: does this help agencies hire better people?' },
    { icon: Shield, title: 'Privacy by design', desc: 'Data protection isn\'t an add-on. It\'s baked into every line of code.' },
    { icon: Zap, title: 'Speed matters', desc: 'From sign-up to first application in under 10 minutes. No bloat, no friction.' },
    { icon: Star, title: 'Relentlessly simple', desc: 'If it takes more than 3 clicks, we redesign it. Complexity is the enemy.' },
  ],
  timeline: [
    { year: '2024', title: 'The idea', desc: 'Our founder spent 6 months at a care agency watching recruiters drown in email CVs.' },
    { year: '2025', title: 'First launch', desc: 'CareHire goes live with 50 beta agencies. 90% retention in the first month.' },
    { year: '2026', title: 'Scale', desc: '30,000+ users, half a million applications processed, and growing fast.' },
  ],
};

const FAQ_CATEGORIES = [
  {
    name: 'Getting Started',
    questions: [
      { q: 'How do I create an account?', a: 'Click "Get started" on our homepage. You\'ll set up your agency name, email, and password. Your unique application link is ready instantly.' },
      { q: 'How long does setup take?', a: 'Most agencies are up and running in under 10 minutes. Create your account, add screening questions, and share your link.' },
      { q: 'Do I need to install anything?', a: 'No. CareHire is fully web-based. It works on any modern browser, desktop or mobile.' },
      { q: 'Can I try it before paying?', a: 'You can explore the dashboard and build your form during signup. A subscription is needed to go live and receive applications.' },
    ],
  },
  {
    name: 'Application Forms',
    questions: [
      { q: 'What types of questions can I ask?', a: 'Short text, long text, yes/no, and multiple choice. You can drag and drop to reorder questions and mark any as required.' },
      { q: 'Can candidates upload a CV?', a: 'Yes. Candidates can upload a PDF, DOC, or DOCX file up to 10MB. CVs are stored securely and downloadable from your dashboard.' },
      { q: 'How do I share my application link?', a: 'Your unique link (carehire.co/apply/your-agency) works anywhere — Facebook, LinkedIn, Indeed, email, or even a QR code.' },
      { q: 'Can I customise the look?', a: 'You can add your agency logo and name. The apply page is branded with your details automatically.' },
    ],
  },
  {
    name: 'Billing & Subscription',
    questions: [
      { q: 'How much does it cost?', a: '$50/month per agency. This includes unlimited applications, custom forms, the full dashboard, email notifications, and CV storage.' },
      { q: 'How do I pay?', a: 'Subscriptions are $50/month and can be activated after you create your account. No setup fees, cancel anytime.' },
      { q: 'Can I cancel anytime?', a: 'Yes. Cancel from your dashboard under Subscription. Your subscription stays active until the end of the current billing period.' },
      { q: 'Are there any hidden fees?', a: 'No. One price, everything included. No setup fees, no per-application charges, no contracts.' },
    ],
  },
  {
    name: 'Data & Privacy',
    questions: [
      { q: 'Is CareHire compliant with data protection laws?', a: 'Yes. We are fully compliant with US and Canadian data protection regulations, including CCPA and PIPEDA. Agencies are data controllers for candidate data; CareHire acts as a data processor. See our Privacy Policy for details.' },
      { q: 'Where is data stored?', a: 'Data is stored with Supabase, our hosting provider, using encrypted databases with row-level security. All data in transit is encrypted via HTTPS/SSL.' },
      { q: 'Can I export my data?', a: 'Yes. Go to Settings > Account and click "Export data". You\'ll receive a download of all your agency data, applications, and form responses.' },
      { q: 'How do I delete my account?', a: 'Go to Settings > Account and click "Delete account". All data is permanently deleted within 30 days. See our Data Retention Policy for details.' },
    ],
  },
  {
    name: 'Technical',
    questions: [
      { q: 'Does it work on mobile?', a: 'Yes. The dashboard and application forms are fully responsive and optimised for mobile devices.' },
      { q: 'What browsers are supported?', a: 'CareHire works on all modern browsers: Chrome, Firefox, Safari, Edge. We recommend keeping your browser updated.' },
      { q: 'Is there an API?', a: 'An API for agencies is on our roadmap. Currently, you can share your application link and use webhooks for notifications.' },
      { q: 'What happens if the service goes down?', a: 'We maintain 99.9% uptime. If there\'s an outage, we post updates on our status page and notify affected users.' },
    ],
  },
];

const HELP_ARTICLES = [
  { icon: FileText, title: 'Creating your first application form', category: 'Getting Started', readTime: '3 min' },
  { icon: Users, title: 'Managing applicants and updating statuses', category: 'Dashboard', readTime: '4 min' },
  { icon: Zap, title: 'Sharing your application link effectively', category: 'Getting Started', readTime: '2 min' },
  { icon: Shield, title: 'Understanding data protection and privacy', category: 'Privacy', readTime: '6 min' },
  { icon: Star, title: 'Writing better screening questions', category: 'Best Practices', readTime: '5 min' },
  { icon: MessageSquare, title: 'Setting up email notifications', category: 'Notifications', readTime: '2 min' },
];

const BLOG_POSTS = [
  {
    title: '5 screening questions every care agency should ask',
    excerpt: 'The right questions filter out unqualified candidates before you even pick up the phone. Here are the five that matter most.',
    date: 'July 10, 2026',
    readTime: '4 min read',
    image: 'https://images.pexels.com/photos/7551667/pexels-photo-7551667.jpeg?auto=compress&cs=tinysrgb&w=800&h=400&fit=crop',
  },
  {
    title: 'How to reduce time-to-hire in social care recruitment',
    excerpt: 'The care sector has a staffing crisis. Speed is everything. Here\'s how leading agencies are cutting their hiring time in half.',
    date: 'July 3, 2026',
    readTime: '6 min read',
    image: 'https://images.pexels.com/photos/5327585/pexels-photo-5327585.jpeg?auto=compress&cs=tinysrgb&w=800&h=400&fit=crop',
  },
  {
    title: 'Data protection for recruitment agencies: a practical guide',
    excerpt: 'Not a lawyer? No problem. This plain-English guide covers what care agencies actually need to do to stay compliant.',
    date: 'June 28, 2026',
    readTime: '8 min read',
    image: 'https://images.pexels.com/photos/5327921/pexels-photo-5327921.jpeg?auto=compress&cs=tinysrgb&w=800&h=400&fit=crop',
  },
];

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full py-4 text-left"
      >
        <span className="text-sm font-semibold text-gray-900">{q}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="pb-4 text-sm text-gray-500 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function InfoPage({ page, onBack, onNavigate }: Props) {
  const [searchQuery, setSearchQuery] = useState('');

  if (page === 'about') {
    return (
      <div className="min-h-screen bg-white">
        <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
            <button onClick={onBack} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div className="flex items-center gap-2 ml-auto">
              <span className="font-bold text-gray-900 text-sm">CareHire</span>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 py-12 pb-20">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">About CareHire</h1>
          <p className="text-lg text-gray-500 leading-relaxed mb-12 max-w-2xl">
            We build recruitment software for care agencies. The kind we wished existed when we watched recruiters
            drown in email CVs and spreadsheets. Simple, fast, and built for the people who actually use it.
          </p>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
            {ABOUT_CONTENT.stats.map((s) => (
              <div key={s.label} className="bg-gray-50 rounded-lg p-5 text-center">
                <p className="text-3xl font-bold text-ink-900">{s.value}</p>
                <p className="text-xs text-gray-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-6">Our values</h2>
          <div className="grid sm:grid-cols-2 gap-5 mb-16">
            {ABOUT_CONTENT.values.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="border border-gray-200 rounded-lg p-6">
                <div className="w-10 h-10 bg-ink-100 rounded-xl flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-ink-900" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-6">Our story</h2>
          <div className="space-y-6 mb-16">
            {ABOUT_CONTENT.timeline.map((item, i) => (
              <div key={item.year} className="flex gap-5">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-ink-900 text-white font-bold text-xs flex items-center justify-center flex-shrink-0">
                    {item.year}
                  </div>
                  {i < ABOUT_CONTENT.timeline.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 mt-2" />}
                </div>
                <div className="pb-6">
                  <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-ink-900 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-3">Want to join us?</h2>
            <p className="text-ink-300 text-sm mb-6 max-w-md mx-auto">
              Start your subscription today and see why 30,000+ users trust CareHire with their recruitment.
            </p>
            <button onClick={onBack} className="px-8 py-3 bg-white text-ink-900 font-bold rounded-xl hover:bg-ink-100 transition-colors">
              Get started
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (page === 'contact') {
    return (
      <div className="min-h-screen bg-white">
        <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
            <button onClick={onBack} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div className="flex items-center gap-2 ml-auto">
              <span className="font-bold text-gray-900 text-sm">CareHire</span>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 py-12 pb-20">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Contact us</h1>
          <p className="text-lg text-gray-500 mb-12 max-w-xl">
            Questions, feedback, or need help? We typically respond within one business day.
          </p>

          <div className="grid md:grid-cols-3 gap-4 mb-12">
            {[
              { icon: Mail, label: 'Email', value: 'carehire@outlook.com', href: 'mailto:carehire@outlook.com' },
              { icon: Phone, label: 'Phone', value: '+1 (555) 123-4567', href: 'tel:+15551234567' },
              { icon: MapPin, label: 'Office', value: '123 Business Park, New York, USA', href: null },
            ].map(({ icon: Icon, label, value, href }) => (
              <div key={label} className="border border-gray-200 rounded-lg p-5">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
                {href ? (
                  <a href={href} className="text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors">{value}</a>
                ) : (
                  <p className="text-sm font-medium text-gray-900">{value}</p>
                )}
              </div>
            ))}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); alert('Message sent! We\'ll get back to you within one business day.'); }} className="space-y-4 max-w-lg">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Name</label>
                <input type="text" required placeholder="Your name" className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <input type="email" required placeholder="you@email.com" className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Subject</label>
              <input type="text" required placeholder="How can we help?" className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Message</label>
              <textarea required rows={5} placeholder="Tell us more..." className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            </div>
            <button type="submit" className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors text-sm">
              Send message
            </button>
          </form>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Company details</h2>
            <div className="text-sm text-gray-500 space-y-1">
              <p><strong className="text-gray-700">Company:</strong> CareHire Ltd</p>
              <p><strong className="text-gray-700">Registered in:</strong> USA</p>
              <p><strong className="text-gray-700">Company number:</strong> 12345678</p>
              <p><strong className="text-gray-700">Registered office:</strong> 123 Business Park, New York, USA</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (page === 'faq') {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
          <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
            <button onClick={onBack} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div className="flex items-center gap-2 ml-auto">
              <span className="font-bold text-gray-900 text-sm">CareHire</span>
            </div>
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-4 py-10 pb-20">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Frequently Asked Questions</h1>
          <p className="text-gray-500 mb-8">Everything you need to know about CareHire.</p>

          <div className="relative mb-10">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search questions..."
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            />
          </div>

          <div className="space-y-8">
            {FAQ_CATEGORIES.map((cat) => {
              const filtered = cat.questions.filter(
                (item) => item.q.toLowerCase().includes(searchQuery.toLowerCase()) || item.a.toLowerCase().includes(searchQuery.toLowerCase())
              );
              if (filtered.length === 0) return null;
              return (
                <div key={cat.name} className="bg-white border border-gray-200 rounded-lg p-6">
                  <h2 className="text-sm font-bold text-gray-900 mb-4">{cat.name}</h2>
                  {filtered.map((item, i) => (
                    <FAQItem key={i} q={item.q} a={item.a} />
                  ))}
                </div>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <p className="text-sm text-gray-500 mb-4">Still have questions?</p>
            <a href="mailto:carehire@outlook.com" className="text-blue-600 font-semibold text-sm hover:underline">
              Email our support team
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (page === 'blog') {
    return (
      <div className="min-h-screen bg-white">
        <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
            <button onClick={onBack} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div className="flex items-center gap-2 ml-auto">
              <span className="font-bold text-gray-900 text-sm">CareHire</span>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 py-10 pb-20">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">CareHire Blog</h1>
          <p className="text-gray-500 mb-10">Insights, tips, and stories for care recruitment agencies.</p>

          <div className="space-y-6">
            {BLOG_POSTS.map((post) => (
              <article key={post.title} className="group cursor-pointer border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-all">
                <div className="flex flex-col sm:flex-row">
                  <div className="sm:w-56 h-40 sm:h-auto flex-shrink-0 overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="p-6 flex-1">
                    <div className="flex items-center gap-3 text-xs text-gray-400 mb-2">
                      <span>{post.date}</span>
                      <span>·</span>
                      <span>{post.readTime}</span>
                    </div>
                    <h2 className="font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">{post.title}</h2>
                    <p className="text-sm text-gray-500 leading-relaxed">{post.excerpt}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Help Centre
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={onBack} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-bold text-gray-900 text-sm">CareHire</span>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-10 pb-20">
        <h1 className="text-3xl font-bold text-gray-900 mb-3">Help Centre</h1>
        <p className="text-gray-500 mb-8">Guides and tutorials to help you get the most out of CareHire.</p>

        <div className="relative mb-10">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search help articles..."
            className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {HELP_ARTICLES.map(({ icon: Icon, title, category, readTime }) => (
            <div key={title} className="bg-white border border-gray-200 rounded-lg p-5 hover:border-blue-200 hover:shadow-sm transition-all cursor-pointer">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-blue-600 font-medium mb-1">{category}</p>
                  <h3 className="text-sm font-bold text-gray-900 mb-1">{title}</h3>
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <Clock className="w-3 h-3" /> {readTime}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-blue-50 border border-blue-100 rounded-lg p-6 text-center">
          <MessageSquare className="w-8 h-8 text-blue-600 mx-auto mb-3" />
          <h2 className="font-bold text-gray-900 mb-1">Need more help?</h2>
          <p className="text-sm text-gray-500 mb-4">Our support team is here for you.</p>
          <a href="mailto:carehire@outlook.com" className="text-blue-600 font-semibold text-sm hover:underline">
            carehire@outlook.com
          </a>
        </div>
      </div>
    </div>
  );
}
